import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonCustom } from "../../components/confirmationArea/ConfirmationArea.styled";
import { PageCard } from "../../components/pageCard/PageCard";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import { FilterButtonGroup } from "../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IExhibition } from "../../interfaces/IExhibition";
import { IListAllParams } from "../../interfaces/IListAllParams";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import {
  ExhibitionCard,
  ExhibitionCardActions,
  ExhibitionCardContent,
} from "./ExhibitionsJudges.styles";

const fields = [
  {
    field: "name",
    label: "Nome",
  },
  {
    field: "details",
    label: "Observação",
  },
];

const STORAGE_KEY = "@ORQUIDEAS:exhibitionsFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

export const ExhibitionsJudges: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [exhibitions, setExhibitions] = useState<IExhibition[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(() => getInitialState("perPage", 12));
  const [currentPage, setCurrentPage] = useState(() =>
    getInitialState("currentPage", 0),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "date"),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "desc"),
  );
  const [loading, setLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", ""),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "containing"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToExhibitionEvaluation = useCallback(
    (exhibitionId: string) => {
      if (!exhibitionId) {
        toastr.error("Exposição não encontrada");
        return;
      }
      navigate(`/exhibition-evaluation/${exhibitionId}`);
    },
    [navigate, toastr],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `exhibitions?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(respose => {
            const responseData = respose.data;

            if (responseData.data?.length > 0) {
              setExhibitions(responseData.data);
              setTotalRows(responseData.totalRows);
              setLoading(false);
            } else {
              setExhibitions([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            if (error.response.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error?.response?.data?.message || "Erro ao listar exposições",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      timeout,
      setLoading,
      setExhibitions,
      setTotalRows,
      setPerPage,
      setCurrentPage,
      signOut,
      navigate,
      toastr,
    ],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(12);
    setOrderField("date");
    setOrderDirection("desc");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>Exposições</TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={() => handleClearFilters()}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div></div>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <Grid
          container
          spacing={2}
          sx={{ marginBottom: "20px", padding: "8px" }}
        >
          {exhibitions.length > 0 &&
            exhibitions.map(exhibition => {
              return (
                <Grid item xs={12} md={4} sm={6} lg={3} key={exhibition.id}>
                  <ExhibitionCard key={exhibition.id}>
                    <ExhibitionCardContent>
                      <h1>{exhibition.name}</h1>

                      <p>
                        {exhibition.date
                          ? dayjs(exhibition.date).format("DD/MM/YYYY")
                          : ""}
                      </p>

                      <p>{exhibition.details || "Sem observação"}</p>

                      <span>
                        {exhibition.city} - {exhibition.stateId}
                      </span>
                    </ExhibitionCardContent>

                    <ExhibitionCardActions>
                      {!exhibition.finished ? (
                        <ButtonCustom
                          sx={{ marginLeft: "8px" }}
                          variant="outlined"
                          color="inherit"
                          onClick={() =>
                            handleToExhibitionEvaluation(exhibition.id || "")
                          }
                        >
                          Iniciar Julgamento
                        </ButtonCustom>
                      ) : (
                        <span style={{ fontSize: "14px" }}>
                          Julgamento Finalizado
                        </span>
                      )}
                    </ExhibitionCardActions>
                  </ExhibitionCard>
                </Grid>
              );
            })}
        </Grid>

        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />
      </PageCard>

      {loading && <BackdropCustom />}
    </>
  );
};
